import { useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import { PrioritySchedulerBoard } from "@miplatform/components";
import { IUserListItemResponse } from "@miplatform/types";
import { Box } from "@mui/material";
import { useCalendarContext } from "../../../providers";
import { CalendarToolBar } from "../CalendarToolBar/CalendarToolBar";
import { useCalendarFilterGroup } from "../CalendarToolBar/useCalendarFilterGroup";
import { WorkOrderAttemptDrawer } from "../WorkOrderAttemptDrawer/WorkOrderAttemptDrawer";
import { buildDateColumns, buildUserColumns } from "../buildColumns.util";

export function SchedulerView({
  assigneeModeUser,
}: Readonly<{
  assigneeModeUser?: IUserListItemResponse;
}>) {
  const { classes } = useStyles();

  const { selectedAttempt, setSelectedAttempt } = useCalendarContext();

  const {
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    builderOptions,
    communityOptions,
    statusOptions,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
  } = useCalendarFilterGroup();

  const {
    assignableUsers,
    calendarSet,
    date,
    events,
    setZoom,
    zoom,
    handleSelectUser,
    selectedUser,
    trayBuckets,
    getTrayDataForCalendar,
  } = useCalendarContext();

  useEffect(() => {
    getTrayDataForCalendar();
  }, [calendarSet?.config?.id]);

  const activeTrays = useMemo(() => {
    if (!trayBuckets?.length) return [];

    const bucketsById = trayBuckets.reduce((acc, bucket) => {
      acc[bucket.id] = bucket;
      return acc;
    }, {} as Record<string, (typeof trayBuckets)[0]>);

    return trayBuckets.reduce((acc, tray) => {
      if (tray.isActive) {
        const bucket = bucketsById[tray.id];
        if (bucket) {
          acc.push(bucket);
        }
      }
      return acc;
    }, [] as (typeof trayBuckets)[0][]);
  }, [trayBuckets]);

  const handleViewMore = (user: IUserListItemResponse) => {
    handleSelectUser(user);
  };

  const selectedAssignee = assigneeModeUser || selectedUser;

  const columns = useMemo(() => {
    if (!assignableUsers) return [];
    if (selectedAssignee) {
      return buildDateColumns({ date, events });
    }
    return buildUserColumns({ users: assignableUsers, events });
  }, [assignableUsers, events, date, selectedAssignee]);

  return (
    <Box className={classes.container}>
      <Box className={classes.topRow}>
        <CalendarToolBar
          zoom={zoom}
          setZoom={setZoom}
          showCalendarFilters={showFilters}
          toggleCalendarFilters={toggleFilters}
          filters={filters}
          appliedFilters={appliedFilters}
          builderOptions={builderOptions}
          communityOptions={communityOptions}
          statusOptions={statusOptions}
          hasPendingFilters={hasPendingFilters}
          onApplyFilters={handleApplyFilters}
          onResetFilters={handleResetFilters}
          handleFilterChange={handleFilterChange}
          title={
            selectedAssignee
              ? `${selectedAssignee.displayName}'s Schedule`
              : "Schedule"
          }
        />
      </Box>
      <Box overflow="auto">
        <PrioritySchedulerBoard
          columns={columns}
          date={date}
          zoom={zoom}
          columnWidth={180}
          hourHeight={60}
          calendarSet={calendarSet?.config ?? null}
          handleViewMore={handleViewMore}
          onCardClick={setSelectedAttempt}
          trays={activeTrays}
        />
      </Box>
      {selectedAttempt && (
        <WorkOrderAttemptDrawer
          key={selectedAttempt.id}
          open={!!selectedAttempt}
          listAttempt={selectedAttempt}
          onClose={() => setSelectedAttempt(null)}
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  topRow: {
    backgroundColor: theme.palette.background.default,
  },
}));
